// ─────────────────────────────────────────────
//  qgendaTraining — the workbook's own list of who is training today.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THE ASSIGNMENT SHEET ABBREVIATES A TRAINEE'S SURNAME TO ONE LETTER, AND
//  THE SAME WORKBOOK SPELLS IT OUT. Measured on "Prelim Anes Assignments
//  9.8.26", the AHP cells write
//
//      "Trevor Stamand 3 orient Kayla G (2)"
//      "Emily Ausloos 3 orient Kaylene L (1)"
//      "Rami Hanano 9 orient Adam B"
//
//  while the `Qgenda Report` tab of that same file carries, under its
//  "Schedule Notes:" heading:
//
//      9/8/2026 Tue Training Gibbons, Kayla Tampa orient
//      9/8/2026 Tue Training Larson, Kaylene Tampa
//      9/8/2026 Tue Training Batz, Adam Tampa
//      9/8/2026 Tue Training Riviere, Jodi-Ann CW
//
//  Owner, 2026-09-07: *"in the assigment sheet sometimes you have the name is
//  short version… make sure that you compair both during the import and place
//  the full name in the system, can be associated to an actual user."*
//
//  ⚠️ WHY THIS IS NOT PART OF qgendaIdentity's COLUMN SCAN. That scan stops
//  dead at "Schedule Notes:" — deliberately, because below it is PROSE that
//  names real people, and a column-pair heuristic run over prose invents
//  colleagues. This block is not prose: it is one strict line shape, so it is
//  read by that shape and by nothing else. A line that does not match is
//  ignored, never guessed at.
//
//  ⚠️ AND IT STATES, IT DOES NOT INFER. "Kayla G" is "Kayla Gibbons" because
//  the department published both spellings this morning, not because a
//  dictionary said so. Two trainees who would share an abbreviation produce a
//  CONFLICT and neither is indexed — the same refusal every other rung makes,
//  because a confident wrong answer puts the wrong person in a room with a
//  patient.
//
//  Pure — no Firebase, no React, no network.
// ─────────────────────────────────────────────

// `… Training Gibbons, Kayla Tampa orient` → last "Gibbons", first "Kayla".
//
// The word "Training" is REQUIRED. The same notes block carries
// "APP - Extra Shift - FT Bulluck, Anthony Approved by: …", which is a shift
// approval and not a trainee, written in exactly the same `Surname, Given`
// register.
const TRAINING_RE =
  /\bTraining\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*)*)\s*,\s*([A-Za-z][A-Za-z'-]*)\b([^|]*)/i;

const str = (v) => String(v ?? "").trim();

/**
 * Every trainee the tab names today.
 *
 * ⚠️ DEDUPED BY LEGAL NAME. The export merges each notes line across the full
 * width of the sheet, so one trainee appears in eight columns of one row.
 *
 * @param {Array<Array<string>>} values raw 2D tab values
 * @returns {Array<{legalLast, legalFirst, legalName, initial, abbreviated,
 *                  note, row}>}
 */
export function parseTrainingRoster(values) {
  if (!Array.isArray(values)) return [];
  const out = [];
  const seen = new Set();
  values.forEach((row, r) => {
    if (!Array.isArray(row)) return;
    row.forEach((cell) => {
      const m = TRAINING_RE.exec(str(cell));
      if (!m) return;
      const legalLast = str(m[1]);
      const legalFirst = str(m[2]);
      if (!legalLast || !legalFirst) return;
      const legalName = `${legalFirst} ${legalLast}`;
      const key = legalName.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        legalLast,
        legalFirst,
        legalName,
        initial: legalLast.charAt(0).toUpperCase(),
        // How the Assignment Sheet writes the same person.
        abbreviated: `${legalFirst} ${legalLast.charAt(0).toUpperCase()}`,
        // Campus and hours ride after the name ("Tampa orient", "WC 10 hours").
        // Carried VERBATIM and never interpreted: "orient" appears on some
        // trainee lines and not on others, and Anthony Cecere is an orientee on
        // the board while his line does not say so. A flag derived from this
        // would have been wrong on the live sheet the day it shipped.
        note: str(m[3]),
        row: r,
      });
    });
  });
  return out;
}

/**
 * The trainees, in the shape `qgendaDisplayIndex` already consumes — so the
 * abbreviation resolves on the SAME rung, with the same conflict handling, as
 * every other name the workbook maps. No second index, no second vocabulary.
 *
 * Each trainee contributes TWO printed spellings: the full name (the sheet uses
 * it too — "Cheryl Martin", "Anthony Cecere") and the abbreviated one.
 */
export function trainingDisplayEntries(roster) {
  const out = [];
  (roster || []).forEach((t) => {
    const base = {
      legalLast: t.legalLast,
      legalFirst: t.legalFirst,
      legalName: t.legalName,
      weakFirst: false,
      shiftToken: "",
      shiftBlock: "Training",
      fromTraining: true,
      row: t.row,
      block: -1,
    };
    out.push({ ...base, displayName: t.legalName, displayRaw: t.legalName });
    if (t.abbreviated && t.abbreviated !== t.legalName) {
      out.push({ ...base, displayName: t.abbreviated, displayRaw: t.abbreviated });
    }
  });
  return out;
}
